import config from '../config';

// ─── Contact Pattern Detection ────────────────────────────
const PHONE_PATTERNS = [
  /(\+?\d{1,4}[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9})/g,
  /(\b0\d{2}[-.\s]?\d{3}[-.\s]?\d{4}\b)/g,              // Zim local
  /(\b\+263\s?\d{2}\s?\d{3}\s?\d{4}\b)/g,                // Zim international
];

const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const URL_PATTERN = /https?:\/\/[^\s]+|www\.[^\s]+/gi;
const HANDLE_PATTERN = /@[\w.]{3,}/g;
const WHATSAPP_PATTERN = /\bwhatsapp\b|\bwa\.me\b/gi;

export interface ContactDetectionResult {
  hasContactInfo: boolean;
  detectedTypes: string[];
  matches: string[];
}

export const detectContactInfo = (text: string): ContactDetectionResult => {
  const detectedTypes: string[] = [];
  const matches: string[] = [];

  // Phone numbers
  for (const pattern of PHONE_PATTERNS) {
    const phoneMatches = text.match(pattern);
    if (phoneMatches) {
      detectedTypes.push('phone number');
      matches.push(...phoneMatches);
    }
  }

  // Emails
  const emailMatches = text.match(EMAIL_PATTERN);
  if (emailMatches) {
    detectedTypes.push('email');
    matches.push(...emailMatches);
  }

  // URLs
  const urlMatches = text.match(URL_PATTERN);
  if (urlMatches) {
    detectedTypes.push('URL');
    matches.push(...urlMatches);
  }

  // Social handles
  const handleMatches = text.match(HANDLE_PATTERN);
  if (handleMatches) {
    detectedTypes.push('contact handle');
    matches.push(...handleMatches);
  }

  // WhatsApp references
  const whatsappMatches = text.match(WHATSAPP_PATTERN);
  if (whatsappMatches) {
    detectedTypes.push('WhatsApp reference');
    matches.push(...whatsappMatches);
  }

  return {
    hasContactInfo: matches.length > 0,
    detectedTypes: [...new Set(detectedTypes)],
    matches: [...new Set(matches)],
  };
};

// ─── Phone Validation ──────────────────────────────────────
export const formatZimPhone = (phone: string): string => {
  // Remove non-digit characters
  let clean = phone.replace(/\D/g, '');

  // Ensure +263 prefix
  if (clean.startsWith('0')) {
    clean = '263' + clean.substring(1);
  }
  if (!clean.startsWith('263')) {
    clean = '263' + clean;
  }

  return '+' + clean;
};

export const isValidZimPhone = (phone: string): boolean => {
  const clean = phone.replace(/\D/g, '');
  // Zim numbers: 263 + 2-digit area/mobile code + 7 digits = 12 digits
  // or 0 + 2 digit + 7 digits = 10 digits local
  if (clean.startsWith('263')) {
    return clean.length === 12;
  }
  if (clean.startsWith('0')) {
    return clean.length === 10;
  }
  return false;
};

// ─── Zimbabwe Location Check ────────────────────────────────
export const isInZimbabwe = (lat: number, lng: number): boolean => {
  const { latMin, latMax, lngMin, lngMax } = config.ZW_BOUNDS;
  return lat >= latMin && lat <= latMax && lng >= lngMin && lng <= lngMax;
};

// ─── Currency Formatting ────────────────────────────────────
export const formatCurrency = (amount: number): string => {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const calculateCommission = (
  dealValue: number,
  rate: number = config.DEFAULT_COMMISSION_RATE,
): number => {
  return dealValue * rate;
};

// ─── Date Formatting ────────────────────────────────────────
export const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-ZW', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleString('en-ZW', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatRelativeTime = (dateStr: string): string => {
  const now = new Date();
  const then = new Date(dateStr);
  const diff = now.getTime() - then.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr);
};
